import { useCallback, useState, type ReactNode } from 'react';

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

type CopyIconButtonProps = {
  text: string;
  ariaLabel: string;
  className?: string;
};

export function CopyIconButton({ text, ariaLabel, className = '' }: CopyIconButtonProps) {
  const [copied, setCopied] = useState(false);
  const canCopy = text.trim() !== '' && text.trim() !== '—';

  const handleCopy = useCallback(async () => {
    if (!canCopy) return;
    const ok = await writeClipboard(text);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [canCopy, text]);

  if (!canCopy) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`shrink-0 p-1 rounded text-textSecondary hover:text-primary hover:bg-primary/10 transition-colors ${className}`}
      aria-label={ariaLabel}
      title={copied ? 'Copiado!' : 'Copiar'}
    >
      {copied ? (
        <i className="ri-check-line text-base text-green-600" />
      ) : (
        <i className="ri-file-copy-line text-base" />
      )}
    </button>
  );
}

type CopyableFieldRowProps = {
  label: string;
  children: ReactNode;
  /** Texto enviado à área de transferência; omitir ou inválido oculta o botão */
  copyText?: string | null;
  className?: string;
};

export function CopyableFieldRow({ label, children, copyText, className = '' }: CopyableFieldRowProps) {
  const canCopy =
    copyText != null &&
    String(copyText).trim() !== '' &&
    String(copyText).trim() !== '—';

  return (
    <div
      className={`flex justify-between gap-3 border-b border-border-subtle py-1.5 text-sm last:border-0 ${className}`}
    >
      <span className="font-instrument text-textSecondary shrink-0">{label}</span>
      <div className="flex items-start justify-end gap-2 min-w-0 flex-1">
        <span className="font-instrument text-textPrimary text-right break-words">{children}</span>
        {canCopy && <CopyIconButton text={String(copyText)} ariaLabel={`Copiar ${label}`} />}
      </div>
    </div>
  );
}
